/**
 * The url a filter link navigates to.
 *
 * This is the legacy `filter` js-module (`public/resources/main/js/modules/filter.js`)
 * restated, and it has to stay that way: the href on the anchor is what a crawler
 * follows and the computed url is what a visitor gets, and the two were already
 * different before this page moved to V2. Making the href itself carry the filter would
 * have handed crawlers a url per filter combination on the most crawled page type on
 * the site, which is the one thing the conversion was not allowed to change.
 *
 * Three behaviours are the legacy module's, not choices:
 *
 * - **`xPage` is dropped.** Page 7 of the unfiltered listing is not page 7 of the
 *   filtered one, and more often than not it does not exist — the legacy page 404s past
 *   its last page.
 * - **The other query parameters survive**, filters included, so the groups compose.
 * - **A filter is set, not toggled.** Picking another value in the same group replaces
 *   it; the way back out is the group's own row that carries a real url of its own.
 *
 * The one departure is deliberate: the legacy module concatenated `href + '?' + params`,
 * which produces a url with two `?` in it whenever the facet already carried a query.
 * Its own query wins here instead. Every facet url in the wild is clean, so this is a
 * guard rather than a change.
 */
export function buildFilterHref(
  href: string,
  parameter: string | null,
  value: string | null,
  search: string
): string {
  const [path, ownQuery = ''] = href.split('?');
  const params = new URLSearchParams(search);

  params.delete('xPage');

  if (parameter) {
    // `$.param` serialises a nested object this way, and PHP reads it back as
    // `$_GET['filters'][$parameter]`.
    params.set(`filters[${parameter}]`, value ?? '');
  }

  new URLSearchParams(ownQuery).forEach((ownValue, key) => params.set(key, ownValue));

  const query = params.toString();

  return query ? `${path}?${query}` : path;
}

/** The parameters that are the listing's own state: its sort, its search, its filters. */
function isListingState(key: string): boolean {
  return key === 'xSort' || key === 'xSearch' || key.startsWith('filters[');
}

/**
 * The url a sort option or a page link navigates to: its own, plus whatever of the
 * listing's state the visitor has already chosen that it does not carry.
 *
 * Almost every listing gets those urls from the backend with the sort and the filters
 * already in them, and for those this returns the href untouched. There are two
 * exceptions. A price band, `/categories/deals/*`: `special-offers.php` reads the band
 * from the path, and `cartOutputData.php` builds that page's pager urls from the path and
 * the filters alone — so without this, a visitor who sorted loses the sort on page two,
 * and one who filtered loses the filter when they sort. And a search within a category:
 * no backend url carries `xSearch`, so page two of "garden" was page two of the whole
 * category.
 *
 * Only the listing's own parameters are carried, so a campaign's `utm_*` stays on the
 * page it landed on, and `xPage` never is: a page link names its own, and a new sort
 * starts from the first page.
 *
 * `choosing` is the parameter the link itself decides — a sort option decides the sort —
 * and the visitor's current value of it is never carried, because that is the value being
 * replaced. It only changes anything for an option whose url leaves the parameter out:
 * search's Best Match, which omits `xSort` for the backend to default. Carrying the old
 * sort into it would leave a price-sorted search sorted by price.
 */
export function carryListingState(href: string, search: string, choosing?: string): string {
  const own = new URLSearchParams(href.split('?')[1] ?? '');
  const carried = new URLSearchParams();

  new URLSearchParams(search).forEach((value, key) => {
    if (isListingState(key) && key !== choosing && !own.has(key)) {
      carried.append(key, value);
    }
  });

  const extra = carried.toString();

  if (extra === '') {
    return href;
  }

  return `${href}${href.includes('?') ? '&' : '?'}${extra}`;
}
